import type { TemplateValues } from '@fieldops/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useRef, useState } from 'react'
import { uploadFile } from '../../lib/files.js'
import { useSession } from '../../lib/session.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { AttachMenu, AttachmentTray, type LockedFile, type TrayFile } from '../compose/AttachmentTray.js'
import { ComposeShell } from '../compose/ComposeShell.js'
import { MessageBody, type TextRange } from '../compose/MessageBody.js'
import { DraftedBanner, MissingFields } from '../compose/Notices.js'
import { RecipientField, isEmail, parseAddressList } from '../compose/RecipientField.js'
import { SendLaterMenu } from '../compose/SendLaterMenu.js'
import { TemplatesMenu } from '../compose/TemplatesMenu.js'
import { withTemplate } from '../compose/text.js'
import { useComposeAssistant, type MessageValue } from '../compose/useComposeAssistant.js'
import { useTextHistory } from '../compose/useTextHistory.js'
import { Icon } from '../Icon.js'
import { Spinner } from '../ui.js'

export { parseAddressList } from '../compose/RecipientField.js'

export interface ComposeContact {
  name: string
  email: string
}

export interface ComposeFile {
  id: string
  name: string
}

export interface ComposeDraft {
  to: string[]
  cc: string[]
  bcc: string[]
  subject: string
  body: string
  attachmentIds: string[]
}

const FALLBACK_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Australia/Adelaide'

function invalid(addresses: string[]): string | null {
  const bad = addresses.find((address) => !isEmail(address))
  return bad ? `${bad} is not an email address.` : null
}

function merge(current: string[], added: string[]): string[] {
  return [...current, ...added.filter((address) => !current.some((item) => item.toLowerCase() === address.toLowerCase()))]
}

/**
 * The one window for writing an email.
 *
 * Every Email, Reply, Send quote and Send invoice button opens this. The
 * caller decides what the message starts as and, for a document, how it is
 * sent. A plain message goes through `inbox.compose`. The Assistant beside it
 * suggests and drafts; it never sends. Send later stores the message and the
 * worker sends it through the same procedure. Decisions 86 and 95.
 */
export function EmailCompose(props: EmailComposeProps) {
  if (!props.open) return null
  return <EmailWindow {...props} />
}

export interface EmailComposeProps {
  open: boolean
  onClose: () => void
  title?: string
  sendLabel?: string
  initial?: Partial<ComposeDraft>
  contacts?: ComposeContact[]
  files?: ComposeFile[]
  /** The quote or invoice PDF, attached by the send itself. */
  locked?: LockedFile[]
  jobId?: string | null
  clientId?: string | null
  replyToMessageId?: string | null
  /** A quote or invoice send, so the Assistant writes a cover note and Send later sends the document. */
  document?: { kind: 'quote' | 'invoice'; id: string } | null
  /** Editing a message already scheduled. */
  scheduled?: { id: string; sendAt: Date | string } | null
  allowBlankTo?: boolean
  subjectOptional?: boolean
  bodyOptional?: boolean
  /** Off for a send that cannot wait in the queue, such as a pick-a-time link. */
  canSendLater?: boolean
  /** A line under the message saying what the send adds to it. */
  note?: string
  /** When set, the caller sends (a document). Otherwise this window does. */
  onSend?: (draft: ComposeDraft) => Promise<void>
  /** After this window has sent a plain message itself. */
  onSent?: () => void
}

function EmailWindow({
  onClose,
  title = 'New email',
  sendLabel = 'Send',
  initial,
  contacts = [],
  files = [],
  locked = [],
  jobId,
  clientId,
  replyToMessageId,
  document,
  scheduled,
  allowBlankTo = false,
  subjectOptional = false,
  bodyOptional = false,
  canSendLater = true,
  note,
  onSend,
  onSent,
}: EmailComposeProps) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()
  const bodyRef = useRef<HTMLTextAreaElement>(null)

  const [to, setTo] = useState<string[]>(initial?.to ?? [])
  const [toText, setToText] = useState('')
  const [cc, setCc] = useState<string[]>(initial?.cc ?? [])
  const [ccText, setCcText] = useState('')
  const [bcc, setBcc] = useState<string[]>(initial?.bcc ?? [])
  const [bccText, setBccText] = useState('')
  const [showCc, setShowCc] = useState((initial?.cc ?? []).length > 0)
  const [showBcc, setShowBcc] = useState((initial?.bcc ?? []).length > 0)
  const history = useTextHistory<MessageValue>({ subject: initial?.subject ?? '', body: initial?.body ?? '' })
  const [range, setRange] = useState<TextRange | null>(null)
  const [picked, setPicked] = useState<string[]>(initial?.attachmentIds ?? [])
  const [uploaded, setUploaded] = useState<TrayFile[]>([])
  const [uploading, setUploading] = useState<string[]>([])
  const [missing, setMissing] = useState<string[]>([])
  const [localError, setLocalError] = useState<string | null>(null)
  const [sending, setSending] = useState(false)

  const compose = useMutation(trpc.inbox.compose.mutationOptions())
  const schedule = useMutation(trpc.compose.schedule.mutationOptions())
  const reschedule = useMutation(trpc.compose.reschedule.mutationOptions())
  const cancelScheduled = useMutation(trpc.compose.cancelScheduled.mutationOptions())

  const attach = useCallback((ids: string[]) => {
    setPicked((current) => [...current, ...ids.filter((id) => !current.includes(id))])
  }, [])

  const context = useComposeAssistantFor({
    autoDraft: !scheduled,
    jobId,
    replyToMessageId,
    document,
    history,
    to,
    contacts,
    attach,
    setRange,
  })
  const { assistant, people } = context
  const data = assistant.data
  const panel = assistant.context.isError || (data && !data.available) ? null : assistant
  // The account's clock, not this computer's; the device's only if the context will not load.
  const timeZone = data?.timeZone ?? (assistant.context.isError ? FALLBACK_ZONE : null)

  const known: TrayFile[] = useMemo(() => {
    const byId = new Map<string, TrayFile>()
    for (const file of files) byId.set(file.id, { id: file.id, name: file.name })
    for (const file of data?.files ?? []) byId.set(file.id, { ...byId.get(file.id), ...file })
    for (const file of uploaded) byId.set(file.id, file)
    return [...byId.values()]
  }, [files, data?.files, uploaded])
  const chosen = known.filter((file) => picked.includes(file.id))

  const everyone = useMemo(() => {
    const seen = new Map<string, ComposeContact>()
    for (const person of contacts) seen.set(person.email.toLowerCase(), person)
    for (const person of people) if (person.email) seen.set(person.email.toLowerCase(), { name: person.name, email: person.email })
    return [...seen.values()]
  }, [contacts, people])

  const value = history.value

  const upload = async (list: File[]) => {
    if (!jobId || list.length === 0) return
    setLocalError(null)
    setUploading((current) => [...current, ...list.map((file) => file.name)])
    for (const file of list) {
      try {
        const saved = await uploadFile(file, { jobId, kind: file.type.startsWith('image/') ? 'PHOTO' : 'DOCUMENT' })
        setUploaded((current) => [
          ...current,
          { id: saved.id, name: saved.filename, sizeBytes: saved.sizeBytes, mimeType: saved.mimeType },
        ])
        attach([saved.id])
      } catch (error) {
        setLocalError(error instanceof Error ? `${file.name}: ${error.message}` : `${file.name} did not upload.`)
      } finally {
        setUploading((current) => {
          const index = current.indexOf(file.name)
          return index === -1 ? current : [...current.slice(0, index), ...current.slice(index + 1)]
        })
      }
    }
    void queryClient.invalidateQueries(trpc.jobs.pathFilter())
    void queryClient.invalidateQueries(trpc.compose.context.pathFilter())
  }

  /** What would go, or the first thing wrong with it. */
  const collect = (): ComposeDraft | null => {
    const toList = merge(to, parseAddressList(toText))
    const ccList = merge(cc, parseAddressList(ccText)).filter(
      (address) => !toList.some((item) => item.toLowerCase() === address.toLowerCase()),
    )
    const bccList = merge(bcc, parseAddressList(bccText))
    const problem =
      (!allowBlankTo && toList.length === 0 ? 'Add someone to send this to.' : null) ??
      invalid(toList) ??
      invalid(ccList) ??
      invalid(bccList) ??
      (!subjectOptional && value.subject.trim() === '' ? 'Add a subject.' : null) ??
      (!bodyOptional && value.body.trim() === '' ? 'Write a message.' : null) ??
      (uploading.length > 0 ? 'Wait for the files to finish uploading.' : null)
    if (problem) {
      setLocalError(problem)
      return null
    }
    return {
      to: toList,
      cc: ccList,
      bcc: bccList,
      subject: value.subject.trim(),
      body: value.body.trim(),
      attachmentIds: chosen.map((file) => file.id),
    }
  }

  const send = async () => {
    const draft = collect()
    if (!draft || sending) return
    setSending(true)
    setLocalError(null)
    try {
      if (scheduled) await cancelScheduled.mutateAsync({ id: scheduled.id })
      if (onSend) {
        await onSend(draft)
      } else {
        await compose.mutateAsync({
          to: draft.to,
          cc: draft.cc.length > 0 ? draft.cc : undefined,
          bcc: draft.bcc.length > 0 ? draft.bcc : undefined,
          subject: draft.subject,
          body: draft.body,
          attachmentIds: draft.attachmentIds.length > 0 ? draft.attachmentIds : undefined,
          jobId: jobId ?? undefined,
          clientId: clientId ?? undefined,
          replyToMessageId: replyToMessageId ?? undefined,
        })
        void queryClient.invalidateQueries(trpc.inbox.pathFilter())
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
        onSent?.()
      }
      void queryClient.invalidateQueries(trpc.compose.pathFilter())
      onClose()
    } catch (error) {
      setLocalError(errorMessage(error))
    } finally {
      setSending(false)
    }
  }

  const sendLater = async (at: Date) => {
    const draft = collect()
    if (!draft) return
    setLocalError(null)
    const row = {
      kind: document ? (document.kind === 'quote' ? ('QUOTE' as const) : ('INVOICE' as const)) : ('EMAIL' as const),
      documentId: document?.id ?? null,
      to: draft.to,
      cc: draft.cc,
      bcc: draft.bcc,
      subject: draft.subject || null,
      body: draft.body || null,
      attachmentIds: draft.attachmentIds,
      jobId: jobId ?? null,
      clientId: clientId ?? null,
      replyToMessageId: replyToMessageId ?? null,
      sendAt: at,
    }
    try {
      if (scheduled) await reschedule.mutateAsync({ ...row, id: scheduled.id })
      else await schedule.mutateAsync(row)
      void queryClient.invalidateQueries(trpc.compose.pathFilter())
      onClose()
    } catch (error) {
      setLocalError(errorMessage(error))
    }
  }

  const applyTemplate = (template: { subject: string | null; bodyText: string | null }, mode: 'replace' | 'insert') => {
    const result = withTemplate(value, range, template, mode, context.templateValues())
    history.commit(result.value)
    setMissing(result.missing)
    setRange({ start: result.caret, end: result.caret })
    requestAnimationFrame(() => bodyRef.current?.focus())
  }

  const canSchedule = canSendLater && (document ? true : Boolean(jobId) || can('INBOX_MANAGE'))
  const writing = assistant.drafting
  const toggles = (
    <>
      {showCc ? null : (
        <button type="button" className="text-[13px] font-semibold text-ink-500 hover:text-ink-900" onClick={() => setShowCc(true)}>
          Cc
        </button>
      )}
      {showBcc ? null : (
        <button type="button" className="text-[13px] font-semibold text-ink-500 hover:text-ink-900" onClick={() => setShowBcc(true)}>
          Bcc
        </button>
      )}
    </>
  )

  return (
    <ComposeShell
      channel="EMAIL"
      title={title}
      heading={data?.heading ?? null}
      assistant={panel}
      drop={{
        enabled: Boolean(jobId),
        hint: jobId ? 'Drop to attach' : 'Link this email to a job to attach a file',
        onFiles: (list) => void upload(list),
      }}
      onClose={onClose}
      footer={
        <>
          <AttachMenu
            jobFiles={known}
            picked={picked}
            canUpload={Boolean(jobId)}
            onToggle={(id) =>
              setPicked((current) => (current.includes(id) ? current.filter((item) => item !== id) : [...current, id]))
            }
            onPick={(list) => void upload(list)}
          />
          <TemplatesMenu channel="EMAIL" hasText={value.body.trim() !== ''} onPick={applyTemplate} />
          <div className="ml-auto flex items-center gap-2">
            {canSchedule ? (
              <SendLaterMenu
                timeZone={timeZone ?? FALLBACK_ZONE}
                scheduledFor={scheduled ? new Date(scheduled.sendAt) : null}
                busy={schedule.isPending || reschedule.isPending}
                disabled={sending || writing || timeZone === null}
                onSchedule={(at) => void sendLater(at)}
              />
            ) : null}
            <button
              type="button"
              disabled={sending || writing}
              className="flex h-9 items-center gap-2 rounded-md bg-brand-500 px-4 text-[14px] font-semibold text-ink-900 hover:bg-brand-550 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={() => void send()}
            >
              {sending ? <Spinner className="size-4" /> : <Icon name="send" className="size-4" />}
              {sendLabel}
            </button>
          </div>
        </>
      }
    >
      <RecipientField
        id="email-compose-to"
        label="To"
        value={to}
        text={toText}
        contacts={everyone}
        placeholder={allowBlankTo ? "Leave blank to use the client's address" : 'name@example.com'}
        trailing={toggles}
        onChange={setTo}
        onText={setToText}
      />
      {showCc ? (
        <RecipientField
          id="email-compose-cc"
          label="Cc"
          value={cc}
          text={ccText}
          contacts={everyone}
          onChange={setCc}
          onText={setCcText}
        />
      ) : null}
      {showBcc ? (
        <RecipientField
          id="email-compose-bcc"
          label="Bcc"
          value={bcc}
          text={bccText}
          contacts={everyone}
          placeholder="Hidden from the other recipients"
          onChange={setBcc}
          onText={setBccText}
        />
      ) : null}
      <div className="flex items-center gap-3 border-b border-ink-100 py-2">
        <label htmlFor="email-compose-subject" className="w-14 shrink-0 text-[13px] text-ink-500">
          Subject
        </label>
        <input
          id="email-compose-subject"
          aria-label="Subject"
          value={value.subject}
          readOnly={writing}
          placeholder={subjectOptional ? 'Left blank, the subject names the document' : 'Subject'}
          className="min-w-0 flex-1 border-0 bg-transparent py-1 text-[15px] font-semibold text-ink-900 outline-none placeholder:font-normal placeholder:text-ink-400"
          onChange={(event) => history.type({ ...value, subject: event.target.value })}
        />
      </div>

      <div className="flex flex-col gap-3 pt-4">
        <DraftedBanner writing={writing} drafted={assistant.drafted} onUndo={assistant.undoDraft} onRedo={assistant.redoDraft} />
        <MessageBody
          id="email-compose-body"
          label="Message"
          value={value.body}
          textareaRef={bodyRef}
          placeholder={
            bodyOptional ? 'A line above the document. The PDF is attached either way.' : 'Write the email'
          }
          range={range}
          busy={assistant.busy}
          readOnly={writing || assistant.busy !== null}
          canRewrite={Boolean(data?.available)}
          onType={(body) => history.type({ ...value, body })}
          onRange={setRange}
          onRewrite={(action, target) => void assistant.rewrite(action, target)}
          onUndo={history.undo}
          onRedo={history.redo}
          onSubmit={() => void send()}
        />
        <MissingFields missing={missing} onDismiss={() => setMissing([])} />
        {note ? <p className="text-[12.5px] text-ink-500">{note}</p> : null}
      </div>

      <div className="mt-auto flex flex-col gap-2 pt-4">
        {localError ? (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-[13px] text-red-800">
            {localError}
          </p>
        ) : null}
        <AttachmentTray
          locked={locked}
          files={chosen}
          uploading={uploading}
          onRemove={(id) => setPicked((current) => current.filter((item) => item !== id))}
        />
      </div>
    </ComposeShell>
  )
}

/** The Assistant for this email, and the merge values for whoever it is to. */
function useComposeAssistantFor({
  autoDraft,
  jobId,
  replyToMessageId,
  document,
  history,
  to,
  contacts,
  attach,
  setRange,
}: {
  autoDraft: boolean
  jobId?: string | null
  replyToMessageId?: string | null
  document?: { kind: 'quote' | 'invoice'; id: string } | null
  history: ReturnType<typeof useTextHistory<MessageValue>>
  to: string[]
  contacts: ComposeContact[]
  attach: (ids: string[]) => void
  setRange: (range: TextRange | null) => void
}) {
  const who = useRef<string | null>(null)
  const assistant = useComposeAssistant({
    target: { channel: 'EMAIL', jobId, replyToMessageId, document },
    history,
    recipientFirstName: () => who.current,
    autoDraft,
    onAttach: attach,
    onRange: setRange,
  })
  const people = assistant.data?.people ?? []

  const first = to[0]?.toLowerCase()
  const person = first ? people.find((row) => row.email?.toLowerCase() === first) : undefined
  const named = first ? contacts.find((row) => row.email.toLowerCase() === first) : undefined
  const name = person?.name ?? named?.name ?? null
  const personFirst = person?.firstName ?? (name ? name.split(/\s+/)[0]! : null)
  who.current = personFirst

  const templateValues = (): TemplateValues => ({
    ...(assistant.data?.values ?? {}),
    ...(name ? { 'contact.name': name, 'contact.firstName': personFirst } : {}),
  })

  return { assistant, people, templateValues }
}
